import { Button } from '@/components/Button/Button'
import { Loader } from '@/components/Loader/Loader'
import { Branch } from '@/components/RepoList/Branch/Branch'
import { RepoCommit } from '@/components/RepoList/RepoCommit/RepoCommit'
import ChevronDownSvg from '@/icons/chevron-down.svg?react'
import { useBranchesState } from '@/state/branches'
import { useCommitsState } from '@/state/commits'
import { useRepoListState } from '@/state/repoList'
import classNames from 'classnames'
import type { FunctionalComponent } from 'preact'
import { useEffect, useState } from 'preact/hooks'
import { useShallow } from 'zustand/shallow'
import styles from './index.module.scss'

export const RepoItem: FunctionalComponent<{
  repoId: number
}> = props => {
  const [active, setActive] = useState(false)
  const fetchCommits = useCommitsState(s => s.fetch)

  const repo = useRepoListState(useShallow(s => (
    s.list.find(repo => repo.id === props.repoId)
  )))

  const shas = useCommitsState(useShallow(
    s => s.byRepoId[props.repoId]?.list.map(item => item.commit.sha) ?? [],
  ))

  const page = useCommitsState(useShallow(s => s.byRepoId[props.repoId]?.page ?? 1))
  const hasMore = useCommitsState(s => (s.byRepoId[props.repoId]?.hasMore))
  const loading = useCommitsState(s => s.loading[props.repoId])
  const setPage = useCommitsState(s => s.setPage)

  const branch = useBranchesState(s =>
    repo ? s.getSelected(`${repo.owner.login}/${repo.name}`, repo.default_branch) : undefined
  )

  useEffect(() => {
    if (repo && active) {
      fetchCommits(props.repoId, repo.owner.login, repo.name, page, branch)
    }
  }, [props.repoId, repo, page, branch, active])

  if (!repo) {
    return null
  }

  return (
    <div
      class={classNames(styles.root, {
        [styles.active]: active,
      })}
    >
      <div class={styles.header}>
        <div
          class={styles.left}
          onClick={() => {
            setActive(!active)
          }}
        >
          <ChevronDownSvg
            class={styles.chevron}
          />

          <div class={styles.name}>
            {repo.full_name}
          </div>
        </div>

        <Branch
          repoId={repo.id}
          repo={repo.name}
          owner={repo.owner.login}
        />
      </div>

      {active && (
        <div class={styles.content}>
          {shas.length === 0 && !loading
            ? (
              <RepoCommit.Item>
                Empty
              </RepoCommit.Item>
            )
            : shas.length === 0 && loading
            ? (
              <RepoCommit.Item>
                Loading <Loader />
              </RepoCommit.Item>
            )
            : null}

          {shas?.map(sha => (
            <RepoCommit
              sha={sha}
              repoId={props.repoId}
            />
          ))}

          <div class={styles.nav}>
            <Button
              disabled={page === 1 || loading}
              onClick={() => {
                setPage(props.repoId, page - 1)
              }}
            >
              Prev
            </Button>
            <Button
              disabled={!hasMore || loading}
              onClick={() => {
                setPage(props.repoId, page + 1)
              }}
            >
              Next
            </Button>
          </div>
        </div>
      )}
    </div>
  )
}
